<template>
  <v-container id="watchlist" fluid tag="section" class="pb-10">
    <WizardAlert section="watchList" alertClass="my-4">
      <template v-slot:text>
        <p>
          {{ $t('views.watchList.wizard.a1') }}
        </p>
        <p>
          {{ $t('views.watchList.wizard.a2') }}
        </p>
        <p>
          {{ $t('views.watchList.wizard.a3') }}
        </p>
      </template>
    </WizardAlert>

    <v-card
      class="px-5 py-2 mt-4 mb-0 elevation-0"
      :class="{ 'sticky-filters': user.preferences.stickyFilters }"
    >
      <PositionsFiltersAndViewMode
        ref="watchesFilters"
        type="watch"
        :positionType.sync="watchType"
        @filterPositions="filterWatches"
      >
        <template v-slot:buttons>
          <div class="d-flex">
            <AddButton v-if="!isMobile" showText :click="createWatch" />
            <MoreButtons :buttons="moreButtons" :text="$t('views.watchList.menu.text')" />
          </div>
        </template>
      </PositionsFiltersAndViewMode>
    </v-card>

    <v-row>
      <v-col cols="12" lg="12" class="mt-4 py-0">
        <v-row>
          <v-col cols="12" md="6" lg="3" class="py-0 mb-4">
            <WatchSummaryCard
              :summary="watchSummary"
              :loadingSummary="loadingSummary"
              type="lastByChangeP"
            />
          </v-col>
          <v-col cols="12" md="6" lg="3" class="py-0 mb-4">
            <WatchSummaryCard
              :summary="watchSummary"
              :loadingSummary="loadingSummary"
              type="firstByChangeP"
            />
          </v-col>
          <v-col cols="12" md="6" lg="3" class="py-0 mb-4">
            <WatchSummaryCard
              :summary="watchSummary"
              :loadingSummary="loadingSummary"
              type="firstByBidPriceDistancePercent"
            />
          </v-col>
          <v-col cols="12" md="6" lg="3" class="py-0 mb-4">
            <WatchSummaryCard
              :summary="watchSummary"
              :loadingSummary="loadingSummary"
              type="firstByAskPriceDistancePercent"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" lg="12" class="mt-0 py-0">
        <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
          <WatchListTable
            ref="watchlistTable"
            :type="watchType !== 'all' ? watchType : undefined"
            :filters="filters"
            @updateFilters="updateFilters"
            @editWatch="editWatch"
            @filterByTag="filterByTag"
            @loadAll="loadAll"
          />
        </v-card>
      </v-col>
    </v-row>

    <WatchModal
      :isModalActive.sync="isWatchModalActive"
      :editItem="editItem"
      :editing="editing"
      @saved="loadAll"
    />
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { getWatchTopSummary } from '@/lib/api.js'
import PositionsFiltersAndViewMode from '@/views/components/common/positions/PositionsFiltersAndViewMode'
import WatchSummaryCard from '@/views/components/watchList/WatchSummaryCard'
import WatchListTable from '@/views/components/watchList/WatchListTable'
import WizardAlert from '@/views/components/common/WizardAlert'
import gtag from '@/lib/gtag'
import visibilityMixin from '@/views/components/core/visibilityMixin'
import AddButton from '@/views/components/common/AddButton'
import MoreButtons from '@/views/components/common/MoreButtons'
import WatchModal from '@/views/components/watchList/WatchModal'

export default {
  components: {
    PositionsFiltersAndViewMode,
    WatchListTable,
    WatchSummaryCard,
    WizardAlert,
    AddButton,
    MoreButtons,
    WatchModal,
  },
  mixins: [visibilityMixin],
  data: () => ({
    watchType: null,
    filters: {},
    loadingSummary: false,
    watchSummary: null,
    isWatchModalActive: false,
    editing: false,
    editItem: {},
  }),
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
    moreButtons() {
      const moreButtons = []
      if (this.isMobile)
        moreButtons.push({
          value: 'add',
          title: this.$t(`views.watchList.menu.createWatch`),
          icon: 'mdi-plus',
          action: this.createWatch,
        })
      moreButtons.push({
        value: 'customHeaders',
        title: this.$t('views.watchList.menu.customHeaders'),
        icon: 'mdi-cog-outline',
        action: this.customHeaders,
      })
      return moreButtons
    },
  },
  watch: {
    watchType: {
      async handler() {
        await this.updateFilters()

        gtag.pageview({
          pagePath: '/watchlist',
          pageTitle: 'watchlist',
          pageContentGroup: 'page',
          extraParams: {
            filter: `type|${this.watchType}`,
          },
        })
      },
    },
  },
  async mounted() {
    this.watchType = 'all'
  },
  methods: {
    async init() {
      const filters = JSON.parse(JSON.stringify(this.filters))

      this.loadingSummary = true
      const watchSummary = await getWatchTopSummary({
        filter: {
          type: this.watchType !== 'all' ? this.watchType : undefined,
          ...filters,
        },
      })
      this.watchSummary = watchSummary.data
      this.loadingSummary = false
    },
    loadAll() {
      if (this.$refs.watchlistTable) this.$refs.watchlistTable.initWatches({ notChangePage: true })
      this.updateFilters()
    },
    async updateFilters() {
      if (this.$refs.watchesFilters) await this.$refs.watchesFilters.updateFilters()
      this.init()
    },
    async filterWatches(filters) {
      this.$set(this, 'filters', JSON.parse(JSON.stringify(filters)))
      this.init(filters)
    },
    filterByTag(tag) {
      if (this.$refs.watchesFilters) this.$refs.watchesFilters.filterByTag(tag)
    },
    customHeaders() {
      if (this.$refs.watchlistTable) this.$refs.watchlistTable.customHeaders()
    },
    async createWatch() {
      this.editing = false
      this.isWatchModalActive = true

      gtag.pageview({
        pagePath: `/watchlist/create`,
        pageTitle: `watchlist|create`,
        pageContentGroup: 'modal',
      })
    },
    editWatch(watch) {
      this.editing = true
      this.isWatchModalActive = true
      this.editItem = JSON.parse(JSON.stringify(watch))

      gtag.pageview({
        pagePath: `/watchlist/edit`,
        pageTitle: `watchlist|edit`,
        pageContentGroup: 'modal',
      })
    },
  },
}
</script>
